// The global "Trust & Certify" nav destination (docs/design/slice4-certify-ux.md
// §2.2): routes to the single eligible app's dashboard when the account has exactly
// one MVP+ app, or a small app picker when there is more than one. The entry point
// itself is visible to every owner/admin on every tier/stage (§2.3) - this screen
// never hides an app from the picker for being below MVP; the destination screen's own
// gate (evaluateCertifyGate) is what shows the lifecycle-gate card for that app.
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AppView } from "@nightcrew/shared";
import { listApps } from "../../api/apps.js";

type State =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "empty" }
  | { readonly status: "redirect"; readonly appId: string }
  | { readonly status: "picker"; readonly apps: readonly AppView[] };

export const CertifyEntry = (): React.JSX.Element => {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    const cancelled = { current: false };
    void (async () => {
      const result = await listApps();
      if (cancelled.current) return;
      if (isErr(result)) {
        setState({ status: "error" });
        return;
      }
      const { apps } = result.value;
      if (apps.length === 0) {
        setState({ status: "empty" });
        return;
      }
      const eligible = apps.filter((a) => a.stage !== "validated");
      const onlyEligible = eligible.length === 1 ? eligible[0] : undefined;
      if (onlyEligible) {
        setState({ status: "redirect", appId: onlyEligible.id });
        return;
      }
      setState({ status: "picker", apps: eligible.length > 0 ? eligible : apps });
    })();
    return () => {
      cancelled.current = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="wrap">
        <div className="card skeleton-card" />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="wrap">
        <div className="card state-card">
          <p>We could not load your apps. Try again.</p>
        </div>
      </div>
    );
  }
  if (state.status === "empty") {
    return (
      <div className="wrap">
        <div className="empty-state">
          <p className="muted">Add an app first, then come back to Certify it.</p>
          <Link className="btn btn-primary" to="/apps/new">
            + Add an app
          </Link>
        </div>
      </div>
    );
  }
  if (state.status === "redirect") {
    return <Navigate to={`/apps/${state.appId}/certify`} replace />;
  }

  return (
    <div className="wrap">
      <h1 className="h2">Trust &amp; Certify</h1>
      <p className="muted mt8">Choose an app to manage its Certify posture.</p>
      <div className="grid mt16" style={{ gap: 12 }}>
        {state.apps.map((app) => (
          <Link key={app.id} className="card app-card" to={`/apps/${app.id}/certify`}>
            <div className="top">
              <span className="app-ico">
                <span className="win" />
              </span>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{app.name}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};
